"use client";

import Image from "next/image";

import Link from "next/link";
import type { CSSProperties } from "react";

import TeamCrest from "@/components/TeamCrest";
import type { MatrixCell } from "@/lib/football";

import {matrixRowTotals} from "@/lib/matrix-totals";

type MatrixTeam = {
  id: number; name: string; city: string; short: string; logo: string;
  colors: string[]; kitPattern: string; clubId?: number | null;
};

type Props = {
  order: { team?: MatrixTeam; index: number }[];
  matrix: MatrixCell[][];
};

function outcomeClass(outcome?: MatrixCell["outcome"]) {
  return outcome === "win" ? "bg-emerald-500/25 text-emerald-100" :
    outcome === "draw" ? "bg-amber-400/20 text-amber-100" :
    outcome === "loss" ? "bg-rose-500/20 text-rose-100" : "text-emerald-200/30";
}

export default function TournamentMatrix({ order, matrix }: Props) {
  const slots = Math.max(1, ...matrix.flatMap((row) => row.map((cell) => cell.results?.length ?? 0)));
  const multiple = slots > 1;
  const opponents=order.map(entry=>entry.index);
  const totals=order.map(entry=>matrixRowTotals(matrix,entry.index,opponents));
  const style = { "--matrix-columns": Math.max(1, order.length) + 4 } as CSSProperties;

  return (
    <section className="glass-card overflow-hidden rounded-2xl" data-testid="tournament-matrix">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-400/15 px-5 py-4">
        <div>
          <h2 className="text-lg font-black uppercase tracking-wide text-white">Шахматка</h2>
          <p className="mt-1 text-xs text-emerald-200/55">Нумерация команд соответствует исходному порядку шахматки; для подгрупп — местам соответствующей таблицы.</p>
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-emerald-200/70" aria-label="Обозначения результатов">
          <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-emerald-500/70" aria-hidden="true" />Победа</span>
          <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-amber-400/70" aria-hidden="true" />Ничья</span>
          <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-rose-500/70" aria-hidden="true" />Поражение</span>
        </div>
      </div>
      <div className="chessboard-surface">
        <div className="chessboard-scroll scrollbar-thin" tabIndex={0} role="region" aria-label="Прокрутка шахматки">
          <table className="chessboard tabular" style={style} data-multiple={multiple} aria-label="Шахматка результатов">
            <caption className="sr-only">Результаты каждой пары команд в порядке итоговых мест. {multiple ? "Два и более результата в ячейке идут по порядку кругов." : "По одной встрече каждой пары."}</caption>
            <colgroup>
              <col style={{ width: "var(--matrix-name-width)" }} />
              {order.map((entry, index) => <col key={entry.team?.id ?? index} style={{ width: "var(--matrix-score-width)" }} />)}
              {["goalsFor","goalsAgainst","goalDiff","points"].map(key=><col key={key} style={{width:"var(--matrix-score-width)"}}/>)}
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="chessboard-name-heading">Команда</th>
                {order.map((entry, index) => (
                  <th scope="col" key={entry.team?.id ?? index} title={`${index + 1}. ${entry.team?.name ?? "—"}`} className="chessboard-number-heading">
                    {index + 1}
                  </th>
                ))}
                {[{label:"ЗМ",title:"Забитые мячи"},{label:"ПМ",title:"Пропущенные мячи"},{label:"РМ",title:"Разница мячей"},{label:"О",title:"Очки"}].map(column=><th key={column.label} scope="col" className="chessboard-number-heading" title={column.title}>{column.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {order.map((entry, i) => (
                <tr key={entry.team?.id ?? i} data-team-id={entry.team?.id}>
                  <th scope="row" className="chessboard-name-cell">
                    <div className="chessboard-team">
                      <span className="chessboard-rank">{i + 1}</span>
                      {entry.team ? <span className="chessboard-crest"><TeamCrest name={entry.team.name} short={entry.team.short} colors={entry.team.colors} logo={entry.team.logo} pattern={entry.team.kitPattern} size={26} /></span> : null}
                      <span className="min-w-0">
                        {entry.team?.clubId ? (
                          <Link href={`/clubs/${entry.team.clubId}`} className="chessboard-team-name transition hover:text-emerald-300 hover:underline">{entry.team.name}</Link>
                        ) : <span className="chessboard-team-name">{entry.team?.name ?? "—"}</span>}
                        {entry.team?.city ? <span className="chessboard-city">{entry.team.city}</span> : null}
                      </span>
                    </div>
                  </th>
                  {order.map((opponent, j) => {
                    if (i === j) return (
                      <td key={j} className="chessboard-diagonal" data-diagonal="true" aria-label={`${entry.team?.name ?? "Команда"}: с собой не играет`}>
                        <Image src="/images/football.svg" width={30} height={30} alt="Футбольный мяч" className="chessboard-ball" />
                      </td>
                    );
                    const cell = matrix[entry.index]?.[opponent.index];
                    const scores = cell?.results ?? (cell?.score ? [{ score: cell.score, outcome: cell.outcome, leg: 1 }] : []);
                    return (
                      <td key={j} className="chessboard-result-cell" data-opponent-id={opponent.team?.id} title={`${entry.team?.name ?? "—"} — ${opponent.team?.name ?? "—"}`}>
                        {Array.from({ length: slots }, (_, slot) => {
                          const result = scores[slot];
                          return (
                            <span key={slot} className={`chessboard-score ${outcomeClass(result?.outcome)}`}
                              title={result ? `${entry.team?.name}: ${result.score}, ${result.leg}-й круг` : "Нет результата"}>
                              {result?.score ?? "—"}
                            </span>
                          );
                        })}
                      </td>
                    );
                  })}
                  {[totals[i].goalsFor,totals[i].goalsAgainst,totals[i].goalDiff>0?`+${totals[i].goalDiff}`:totals[i].goalDiff,totals[i].points].map((value,index)=><td key={`total-${index}`} className="chessboard-result-cell" style={{textAlign:"center",fontWeight:700,color:index===3?"#6ee7b7":undefined,borderLeft:index===0?"1px solid rgba(52,211,153,.25)":undefined}}>{value}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="border-t border-emerald-400/10 px-5 py-3 text-xs leading-relaxed text-emerald-200/55">
        ЗМ — забитые, ПМ — пропущенные, РМ — разница мячей, О — очки. Итоги рассчитаны по показанным результатам: 3 очка за победу, 1 за ничью. Несыгранные встречи не учитываются. Счёт указан со стороны команды в строке. {multiple ? "Верхняя и нижняя строки — отдельные встречи по кругам. " : ""}
        Номера столбцов соответствуют порядку команд в текущей шахматке. На узком экране прокручивается только шахматка — названия остаются закреплены.
      </p>
    </section>
  );
}
